import { InfiniteScroll } from 'libreact/lib/InfiniteScroll';
import React, { FC, ReactNode, useMemo } from 'react';
import { groupBy } from 'lodash-es';

import { Box, Flex, Grid } from 'components/styled';
import Text from 'components/styled/Text';
import LoaderBounce from 'components/loaders/LoaderBounce';
import Paper from 'components/styled/Paper';

import { Loader } from 'modules/loader';
import { FileSearchingImage } from 'modules/notFound/NotFoundImage';

type Props = {
	isEmpty: boolean;
	isLoading?: boolean;
	noResults?: string | ReactNode;
	fullWidthItems?: boolean;
	tileSize?: string;
	groupFunction?: (n: ReactNode) => string | undefined;
	wrapper?: (children: ReactNode) => JSX.Element;
	children: ReactNode;
};

const TileGrid: FC<Props> = ({
	isEmpty,
	isLoading,
	noResults,
	fullWidthItems,
	tileSize = '320px',
	children,
	wrapper = c => <>{c}</>,
	groupFunction,
}) => {
	const groups = useMemo(
		() =>
			groupBy(
				React.Children.map(children, child => child),
				child => groupFunction?.(child) ?? 'ungroupped',
			),
		[children, groupFunction],
	);

	return wrapper(
		<>
			{isLoading && (
				<Box py={4}>
					<Loader />
				</Box>
			)}
			{!isLoading && isEmpty && (
				<Flex
					flexDirection="column"
					justifyContent="center"
					flexGrow={1}
					my={3}
				>
					{typeof noResults === 'string' ? (
						<Paper alignItems="center" justifyContent="center">
							<Text fontWeight="bold" textAlign="center" fontSize="xl">
								{noResults}
							</Text>

							<FileSearchingImage />
						</Paper>
					) : (
						noResults
					)}
				</Flex>
			)}
			{!isLoading &&
				!isEmpty &&
				Object.keys(groups).map(k => (
					<Flex key={k} flexDirection="column">
						{k !== 'ungroupped' && (
							<Text fontSize="26px" fontWeight={800} p={3}>
								{k}
							</Text>
						)}
						<Box width="100%">
							<Grid
								key={k}
								gridGap={4}
								gridTemplateColumns={[
									`1fr`,
									`repeat(auto-fill, ${
										fullWidthItems ? '1fr' : `minmax(${tileSize}, 1fr)`
									})`,
								]}
								justifyItems={fullWidthItems ? 'stretch' : 'center'}
								alignItems="stretch"
							>
								{groups[k].map(c => c)}
							</Grid>
						</Box>
					</Flex>
				))}
		</>,
	);
};
export default TileGrid;

type InfiniteGridProps = {
	page: number;
	fetchMore: () => void;
	hasMore: boolean;
} & Omit<Props, 'wrapper'>;

export const InfiniteGrid: FC<InfiniteGridProps> = ({
	isLoading,
	page,
	fetchMore,
	hasMore,
	children,
	...props
}) => (
	<TileGrid
		// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
		isLoading={isLoading || page === -1}
		wrapper={c => (
			<InfiniteScroll
				cursor={page}
				loadMore={fetchMore}
				hasMore={hasMore}
				sentinel={
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							width: '100%',
							marginTop: 32,
							marginBottom: 32,
						}}
					>
						<LoaderBounce />
					</div>
				}
			>
				{c}
			</InfiniteScroll>
		)}
		{...props}
	>
		{children}
	</TileGrid>
);
